// `useJobStatus(id)` — TanStack Query hook backing /app/jobs/:id.
//
// Status-aware polling:
//   running | pending → refetch every 3s (matches v1.0 cadence)
//   completed | failed → refetchInterval returns false; polling auto-stops
//
// Pitfall 14 mitigation (JOBS-03): TanStack Query dedupes by queryKey.
// Under React 19 StrictMode dev double-mount, only ONE refetchInterval
// fires per ['jobStatus', id] key. Verified by integration test in
// Plan 07-04.
//
// `id` may be `null` when the route's URL param failed to parse (NaN, 0,
// negative). In that case `enabled` short-circuits the query so the route
// renders the not-found panel without an HTTP round-trip.
//
// `staleTime: 0` — the detail page is the polling focus; we always refetch
// on focus while polling so the operator sees the freshest agent_runs row.
// `refetchIntervalInBackground: false` — when the tab is hidden, browsers
// throttle setInterval anyway; explicit `false` makes the behavior obvious
// and avoids burning DB queries on background tabs.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { fetchJobStatus, ApiError } from '@/lib/api';
import type { JobDetailDTO } from '@/types/db';

const POLL_INTERVAL_MS = 3000;

export function useJobStatus(
  id: number | null,
  enabled: boolean = true,
): UseQueryResult<JobDetailDTO, Error> {
  const isValidId = id != null && Number.isFinite(id) && id > 0;
  return useQuery<JobDetailDTO, Error>({
    queryKey: ['jobStatus', id ?? -1],
    queryFn: ({ signal }) => fetchJobStatus(id as number, signal),
    enabled: enabled && isValidId,
    staleTime: 0,
    refetchInterval: (query) => {
      const data = query.state.data as JobDetailDTO | undefined;
      if (!data) return false;
      return data.status === 'running' || data.status === 'pending'
        ? POLL_INTERVAL_MS
        : false;
    },
    refetchIntervalInBackground: false,
    retry: (failureCount, error) => {
      // 404 is terminal — the job doesn't exist; don't retry.
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 1; // matches global retry: 1
    },
  });
}
